// React's side of the registry: a card follows the one constraint type it
// honours, and the owner for labels. Kept in its own entry so the API's tests
// never load React.
import * as React from 'react';
import { DEFAULTS, constraintScopeKey, type ConstraintOf, type ConstraintType, type ConstraintScope } from './types.ts';
import { CONSTRAINTS_API, type ConstraintOwner, type ConstraintRegistry } from './registry.ts';

/** Anything that hands out APIs by name, such as a pilet's `app`. */
export interface ApiSource {
  getApi<T>(name: string): Promise<T>;
}

/** The registry once the `constraints` API is there; `undefined` until then, or without it. */
export function useConstraintRegistry(app: ApiSource): ConstraintRegistry | undefined {
  const [registry, setRegistry] = React.useState<ConstraintRegistry>();
  React.useEffect(() => {
    let alive = true;
    void app.getApi<ConstraintRegistry>(CONSTRAINTS_API).then((r) => alive && setRegistry(r));
    return () => {
      alive = false;
    };
  }, [app]);
  return registry;
}

/** The combined value of one type on a scope; the default while there is no registry or scope. */
export function useConstraint<T extends ConstraintType>(registry: ConstraintRegistry | undefined, scope: ConstraintScope | undefined, type: T): ConstraintOf<T> {
  const key = scope ? constraintScopeKey(scope) : undefined;
  const subscribe = React.useCallback(
    (notify: () => void) => (registry && scope ? registry.subscribe(scope, type, notify) : () => undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, key, type],
  );
  const cache = React.useRef<{ json?: string; value?: ConstraintOf<T> }>({});
  const read = (): ConstraintOf<T> => {
    const value = registry && scope ? registry.current(scope, type) : DEFAULTS[type];
    const json = JSON.stringify(value);
    if (cache.current.json !== json) cache.current = { json, value };
    return cache.current.value as ConstraintOf<T>;
  };
  return React.useSyncExternalStore(subscribe, read, read);
}

/** The latest registration on a scope, for labels such as the board tab's. */
export function useConstraintOwner(registry: ConstraintRegistry | undefined, scope: ConstraintScope | undefined): ConstraintOwner | undefined {
  const key = scope ? constraintScopeKey(scope) : undefined;
  const subscribe = React.useCallback(
    (notify: () => void) => (registry && scope ? registry.subscribeOwner(scope, notify) : () => undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, key],
  );
  const cache = React.useRef<{ json?: string; value?: ConstraintOwner }>({});
  const read = (): ConstraintOwner | undefined => {
    const value = registry && scope ? registry.owner(scope) : undefined;
    const json = JSON.stringify(value ?? null);
    if (cache.current.json !== json) cache.current = { json, value };
    return cache.current.value;
  };
  return React.useSyncExternalStore(subscribe, read, read);
}
